import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '../../lib/cn.ts';

/**
 * A panel under a button, on the native <details>: opens and closes with the button,
 * and also closes when tapping or clicking outside it, and with Escape (focus goes back
 * to the button). On a phone the panel opens in the page flow (it pushes the content
 * down instead of covering it), so whatever the user taps outside stays reachable; the
 * tap reaches its target first and then the panel closes (a `click` listener, not
 * `pointerdown`, so nothing moves under the finger). No injected styles (strict CSP).
 */
export function Dropdown({
  label,
  children,
  className,
  panelClassName,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
  panelClassName?: string;
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = (focusButton: boolean) => {
      const details = ref.current;
      if (!details) return;
      details.open = false;
      if (focusButton) details.querySelector('summary')?.focus();
    };
    const onClick = (event: MouseEvent) => {
      if (event.target instanceof Node && !ref.current?.contains(event.target)) close(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      }
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <details
      ref={ref}
      className={cn('relative', className)}
      onToggle={(event) => {
        setOpen(event.currentTarget.open);
      }}
    >
      <summary className="inline-flex min-h-9 cursor-pointer list-none items-center gap-2 rounded-lg border border-border bg-surface px-3 text-sm font-medium hover:bg-muted">
        {label}
      </summary>
      <div
        className={cn(
          'mt-1 rounded-lg border border-border bg-surface p-2 sm:absolute sm:right-0 sm:z-20 sm:shadow-lg',
          panelClassName,
        )}
      >
        {children}
      </div>
    </details>
  );
}
